<!-- src/components/PageLoader.vue -->
<template>
  <Teleport to="body">
    <!-- Full page loader overlay -->
    <Transition name="loader-fade">
      <div v-if="showFullLoader" class="page-loader-overlay">
        <div class="loader-content">
          <div class="loader-spinner"></div>
          <p class="loader-message">{{ loadingMessage }}</p>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import { usePageLoaderStore } from '@/stores/usePageLoader'
import NProgress from 'nprogress'
import 'nprogress/nprogress.css'

const router = useRouter()
const loader = usePageLoaderStore()

const showFullLoader = computed(() => loader.showFullLoader)

// Provide a loading message, either static or from the store if available
const loadingMessage = computed(() => loader.loadingMessage ?? 'Loading...')

// Configure NProgress
NProgress.configure({ 
  showSpinner: false,
  trickle: true,
  trickleSpeed: 200,
  minimum: 0.08,
  easing: 'ease',
  speed: 500
})

// Watch for full loader changes
watch(() => loader.showFullLoader, (show) => {
  if (show) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
})

// Router navigation hooks
router.beforeEach((to, from, next) => {
  // Skip loader for hash changes and same-page navigation
  if (to.path === from.path && to.hash !== from.hash) {
    next()
    return
  }
  
  // Start progress bar
  NProgress.start()
  loader.setLoading(true)
  next()
})

router.afterEach(() => {
  // Complete progress bar
  NProgress.done()
  loader.setLoading(false)
})

// Handle router errors
router.onError(() => {
  NProgress.done()
  loader.setLoading(false)
})

onMounted(() => {
  // Hide any lingering loader on mount
  NProgress.done()
})

onUnmounted(() => {
  // Cleanup
  document.body.style.overflow = ''
  NProgress.remove()
})
</script>

<style scoped>
.page-loader-overlay {
  position: fixed;
  inset: 0;
  background: color-mix(in srgb, var(--background) 95%, transparent 5%);
  backdrop-filter: blur(8px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  will-change: opacity;
}

.loader-content {
  text-align: center;
  padding: 2rem;
}

.loader-spinner {
  width: 48px;
  height: 48px;
  margin: 0 auto 1.5rem;
  border: 3px solid var(--border-color);
  border-top-color: var(--primary-action);
  border-radius: 50%;
  animation: spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
  will-change: transform;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loader-message {
  color: var(--text-color);
  font-size: 1.1rem;
  margin: 0;
  opacity: 0.9;
  animation: pulse-text 2s ease-in-out infinite;
}

@keyframes pulse-text {
  0%, 100% { opacity: 0.7; }
  50% { opacity: 1; }
}

/* Transition animations */
.loader-fade-enter-active,
.loader-fade-leave-active {
  transition: opacity 0.3s ease;
}

.loader-fade-enter-from,
.loader-fade-leave-to {
  opacity: 0;
}
</style>